import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LoadingOverlay from '../../../../components/feedback/LoadingOverlay'
import { useSnackBar } from '../../../../components/feedback/SnackBarProvider'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import { KindEditPageProps } from '../KindPages'
import { getEntityLinkFromResourceKey, getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import { logException } from '../../../../util/Logging'
import FileInput from '../../../../components/input/FileInput'
import computeLabel from '../shared/computeLabel'
import useUploadResource from '../../../../io/useUploadResource'
import useResource from '../../../../io/useResource'

const RuntimeEditPage = ({
  entity,
  entityId,
  entityType,
  entityTypes,
  entityRepository,
  loading,
}: KindEditPageProps) => {
  const {
    fields,
    updateField,
    submitting,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerPreSubmit,
  } = useEntityEdit({ entity, entityId, entityType, entityRepository })

  const { upload } = useUploadResource()
  const { showSnackBar } = useSnackBar()

  const existingResourceId = entity ? getResourceKeyFromEntity(entity) : undefined
  const { isLoading: resourceLoading, error: resourceError } = useResource(existingResourceId)
  const fileRef = useRef<File | null>(null)
  const [fileSelected, setFileSelected] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')

  const isReplacing = !!existingResourceId && fileSelected

  const handleFileChange = (file: File) => {
    fileRef.current = file
    setFileSelected(true)
  }

  const handleSave = () => {
    if (isReplacing) {
      setConfirmationText('')
      setDialogOpen(true)
    } else {
      submitEntity()
    }
  }

  const handleConfirm = () => {
    setDialogOpen(false)
    submitEntity()
  }

  const preSubmit = useCallback(async () => {
    const file = fileRef.current
    if (!file && !existingResourceId) return []
    if (!file) return []

    try {
      const resourceKey = await upload({ data: file, extension: 'js' })
      return [getEntityLinkFromResourceKey(resourceKey)]
    } catch (e) {
      logException(e)
      showSnackBar('Failed to upload runtime', 'error')
      throw e
    }
  }, [upload, showSnackBar, existingResourceId])

  useEffect(() => {
    registerPreSubmit(preSubmit)
  }, [registerPreSubmit, preSubmit])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const label = computeLabel(entityType, fields)

  const requiredConfirmationText = 'backwards compatible'

  return (
    <>
      <PageLayout
        title={label ?? entityType.name}
        subtitle={label ? entityType.name : undefined}
        showCancel
        showSave
        showDelete={!!entity}
        saveDisabled={!valid || resourceLoading}
        onSave={handleSave}
        onDelete={deleteEntity}
      >
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={loading}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Runtime script</Typography>
          <FileInput onChange={handleFileChange} accept=".js" />
          {resourceError && <Alert severity="error">Failed to load existing runtime script.</Alert>}
          {existingResourceId && !fileSelected && !resourceError && (
            <Typography variant="body2" color="text.secondary">
              A runtime script is already uploaded for this entity. Select a new JavaScript file to replace it.
            </Typography>
          )}
          {isReplacing && (
            <Alert severity="warning">
              Replacing a runtime affects all entities using it. Ensure backwards compatibility before proceeding.
            </Alert>
          )}
        </Stack>
      </PageLayout>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm runtime replacement</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Replacing a runtime affects all entities using it. Type <strong>{requiredConfirmationText}</strong> to
            confirm the change won't break existing assets.
          </DialogContentText>
          <TextField
            fullWidth
            label="Confirmation"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirm} color="error" disabled={confirmationText !== requiredConfirmationText}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingOverlay isOpen={submitting || resourceLoading} />
    </>
  )
}

export default RuntimeEditPage
